import { Component, OnInit, ViewChild, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Table } from 'primeng/table';
import { StatusPipe } from '@src/app/shared/pipes/status.pipe';
import { ShipmentItemState } from '@app/shared/enums/ShipmentItemState.enum';
import { ShipmentItemStatePipe } from '@src/app/shared/pipes/shipmentItemState.pipe';
import { ConfirmationService } from 'primeng/api';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { ShipmentsService, RemoveShipmentItemListRequest, ShipmentItemListRequest, CancelShipmentRequest, ReviewShipmentItemRequest, ShipmentDetailsResponse
} from '@src/app/shared/backend/clients';
import { FriendlyBooleanPipe } from '@src/app/shared/pipes/friendlyBoolean.pipe';
import { spacesValidator } from '@src/app/shared/validators/trimSpaces.validator';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { DialogReasonComponent } from '@src/app/shared/components/dialog-reason/dialog-reason.component';
import { FilterOperator } from '@src/app/shared/enums/FilterOperator.enum';
import { SessionService } from '@app/shared/services/session.service';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';
import { InventoryMultiScannerItem, MultiScannerComponent } from '@src/app/shared/components/multi-scanner/multi-scanner.component';

@Component({
    selector: 'app-view-shipment',
    templateUrl: './view-shipment.component.html',
    styleUrl: './view-shipment.component.scss',
    host: {
        class: 'app-view-shipment'
    },
    providers: [StatusPipe, ConfirmationService, FriendlyBooleanPipe, ShipmentItemStatePipe],
    standalone: false
})
export class ViewShipmentComponent extends BaseViewComponent implements OnInit, OnDestroy {

  private readonly destroyedComponent$ = new Subject<void>();

  @ViewChild('dialog') dialog: DialogReasonComponent | undefined;

  delete: boolean = false;
  allowModifyShipment: boolean = false;
  allowViewRetailer: boolean = false;
  allowViewUser: boolean = false;

  shipmentId: number = 0;
  shipmentData: ShipmentDetailsResponse = {
    id: 0,
    name: '',
    totalItems: 0,
    isCancelled: false,
    isReceived: false,
    retailerId: 0,
    retailerName: '',
    createdOn: '',
    updatedOn: '',
    createdById: '',
    updatedById: '',
    createdByFullName: '',
    updatedByFullName: ''
  };

  shipmenItemState = ShipmentItemState;

  displayAddItemsModalbox: boolean = false;
  displayShipmentItemDetailsModalbox: boolean = false;
  displayRemoveItemConfirmationModalbox: boolean = false;
  displayCancelShipmentModalbox: boolean = false;
  displayUnsavedScannerItemsModalbox: boolean = false;

  scannerMounted: boolean = false;
  scannerInitialItems: string[] = [];

  selectedItemId: number | undefined;
  companyHasCpo: boolean = false;

  shipmentItemId: number = 0;
  itemsList: any;
  isResultsEmpty: boolean = true;
  inMemoryPagedResults: any[] = [];
  inMemoryTotalRecords: number = 0;
  inMemoryPaginator = { first: 0, rows: 5 };

  filterOperator = FilterOperator;

  @ViewChild('dtShipmentContentTable') table: Table | undefined;
  @ViewChild('multiScanner') multiScanner?: MultiScannerComponent;

  // local pagination
  paginatorFirst = 0;
  paginatorRows = 10;

  addItemsFormGroup: FormGroup = this.fb.group({
    shipmentItems: [[], Validators.required],
    serialNumber: ['', spacesValidator()]
  });

  public cancelShipmentForm: FormGroup = this.fb.group({
    reason: ['', [Validators.required, Validators.maxLength(50)]],
  });

  constructor(
    private permissionService: PermissionService,
    private router: Router,
    private route: ActivatedRoute,
    private shipmentsService: ShipmentsService,
    private session: SessionService,
    private changeDetector: ChangeDetectorRef,
    private fb: FormBuilder) { super(); }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {

    this.route.params
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(async (params) => {
        this.shipmentId = Number(params['shipmentId']);
        if (this.shipmentId > 0) {
          await this.get();
        }
      });

    this.session.currentCompanyData$
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(company => {
        this.companyHasCpo = !!company?.hasCpo;
      });

    this.delete = await this.permissionService.hasActionPermissions(Actions.delete);
    this.allowModifyShipment = await this.permissionService.hasActionPermissions(Actions.modify_shipments);
    this.allowViewRetailer = await this.permissionService.hasActionPermissions(Actions.view_retailers);
    this.allowViewUser = await this.permissionService.hasActionPermissions(Actions.view_users);

    this.addItemsFormGroup.get('serialNumber')?.valueChanges
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(value => {
        const rawValue = String(value ?? '');
        const trimmedValue = rawValue.trim();

        if (rawValue !== trimmedValue) {
          this.addItemsFormGroup.get('serialNumber')?.setValue(trimmedValue, { emitEvent: false });
        }
      });
  }

  override async get(): Promise<void> {
    try {
      this.shipmentData = await firstValueFrom(
        this.shipmentsService.apiShipmentsDetailsIdGet(this.shipmentId)
      );
    } catch (error) {
      console.error('Error fetching shipment data:', error);
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  get totalRecords(): number {
    return this.shipmentData?.shipmentItems?.length ?? 0;
  }

  get paginatedItems(): any[] {
    const items = this.shipmentData?.shipmentItems ?? [];
    const startIndex = this.paginatorFirst;
    const endIndex = startIndex + this.paginatorRows;

    return items.slice(startIndex, endIndex);
  }

  onPageChange(event: any): void {
    this.paginatorFirst = event.first;
    this.paginatorRows = event.rows;
  }

  get paginatorPageNumber(): number {
    const rows = this.paginatorRows || 1;

    // convert local 0-based item offset to the 1-based paginator expected page number
    return Math.floor((this.paginatorFirst ?? 0) / rows) + 1;
  }

  addShipmentItems(): void {
    this.scannerInitialItems = this.getSelectedSerialNumbers();
    this.displayAddItemsModalbox = true;
  }

  onScannerDialogShow(): void {
    this.scannerMounted = true;
    this.changeDetector.detectChanges();
  }

  async onScannerDialogHide(): Promise<void> {
    await this.multiScanner?.stopScanner();
    this.scannerMounted = false;
  }

  async onSubmitAddedItems(): Promise<void> {
    if (!this.addItemsFormGroup.valid) return;

    const serialNumbersObj = this.addItemsFormGroup.controls['shipmentItems'].value ?? [];
    const serialNumberList = serialNumbersObj.map((item: { serialNumber: string }) => item.serialNumber);

    const payload: ShipmentItemListRequest = {
      itemList: serialNumberList
    };

    await firstValueFrom(this.shipmentsService.apiShipmentsIdItemsAddPut(this.shipmentId, payload));

    this.displayAddItemsModalbox = false;
    this.resetScannerDraft();
    this.resetShipmentItemsPaginator();

    await this.get();
  }

  async onCancelItemAdd(): Promise<void> {
    await this.multiScanner?.stopScanner();

    this.displayAddItemsModalbox = false;
    this.resetScannerDraft();
  }

  async revalidateShipmentItems(): Promise<void> {
    try {
      this.shipmentData = await firstValueFrom(
        this.shipmentsService.apiShipmentsIdRevalidateGet(this.shipmentId)
      );
    } catch (error) {
      console.error('Error revalidating shipment items:', error);
    } finally {
      this.changeDetector.detectChanges();
    }

    await this.get();
  }

  goToEditShipment(): void {
    this.router.navigate([`/shipments/${this.shipmentId}/edit`]);
  }

  getSelectedItems(): any[] {
    return this.addItemsFormGroup.get('shipmentItems')?.value ?? [];
  }

  addToPayload(): void {
    const serialNumber = this.addItemsFormGroup.get('serialNumber')?.value?.trim();

    if (serialNumber && !this.getSelectedItems().some((item: any) => item.serialNumber === serialNumber)) {
      const selectedItems = [...this.getSelectedItems(), { serialNumber }];

      this.addItemsFormGroup.patchValue({ shipmentItems: selectedItems });
      this.addItemsFormGroup.get('serialNumber')?.reset();
    }
  }

  removeItemChip(item: any): void {
    const selectedItemsList = [...this.getSelectedItems()];
    const indexToRemove = selectedItemsList.indexOf(item);

    if (indexToRemove !== -1) {
      selectedItemsList.splice(indexToRemove, 1);
      this.addItemsFormGroup.patchValue({ shipmentItems: selectedItemsList });
    }
  }

  onRemoveItemFromShipment(event: any, id: number): void {
    this.selectedItemId = id;
    this.displayRemoveItemConfirmationModalbox = true;
  }

  get shipmentItemCount(): number {
    const shipmentItemsControl = this.addItemsFormGroup.get('shipmentItems');

    return shipmentItemsControl?.value?.length || 0;
  }

  onCancelItemRemoval(): void {
    this.displayRemoveItemConfirmationModalbox = false;
    this.selectedItemId = undefined;
  }

  async onConfirmItemRemoval(): Promise<void> {
    if (!this.selectedItemId) {
      this.displayRemoveItemConfirmationModalbox = false;
      this.selectedItemId = undefined;
      return;
    }

    try {
      const payload: RemoveShipmentItemListRequest = {
        itemListToRemove: [this.selectedItemId],
      };

      await firstValueFrom(
        this.shipmentsService.apiShipmentsIdItemsRemovePut(this.shipmentId, payload)
      );
    } catch (error) {
      console.error('Error removing shipment item:', error);
    } finally {
      this.displayRemoveItemConfirmationModalbox = false;
      this.selectedItemId = undefined;
      await this.get();
    }
  }

  async showDeleteDialog(): Promise<void> {
    const deleteCheckResponses = await firstValueFrom(this.shipmentsService.apiShipmentsDeleteCheckBatchPost([this.shipmentId]));

    this.dialog?.show(deleteCheckResponses);
  }

  async deleteShipment(doDelete: boolean): Promise<void> {
    if (doDelete) {
      await firstValueFrom(this.shipmentsService.apiShipmentsDeleteIdDelete(this.shipmentId));
      this.router.navigate([`/shipments`]);
    }
  }

  cancelShipment(): void {
    this.displayCancelShipmentModalbox = true;
  }

  onCancelCancelShipmentModal(): void {
    this.displayCancelShipmentModalbox = false;
    this.cancelShipmentForm.reset();
  }

  async onSubmitCancelShipmentForm(): Promise<void> {
    if (this.cancelShipmentForm.invalid) return;

    const payload: CancelShipmentRequest = {
      reason: this.cancelShipmentForm.controls['reason'].value,
    };

    try {
      await firstValueFrom(
        this.shipmentsService.apiShipmentsIdCancelPut(this.shipmentId, payload)
      );
    } catch (error) {
      console.error('Error canceling shipment:', error);
    } finally {
      this.displayCancelShipmentModalbox = false;
      this.cancelShipmentForm.reset();
      await this.get();
    }
  }

  async viewShipmentItemDetails(event: any, serialNumber: string, shipmentItemId: number): Promise<void> {
    try {
      const itemsPage = await firstValueFrom(
        this.shipmentsService.apiShipmentsReviewCandidateIdSerialNumberGet(this.shipmentId, serialNumber)
      );

      this.itemsList = itemsPage.results || [];
      this.isResultsEmpty = this.itemsList.length === 0;
      this.resetInMemoryPaginatorAndSlice();

    } catch (error) {
      console.error('Error validating serial number', error);
      this.itemsList = [];
      this.isResultsEmpty = true;
      this.updateInMemoryPageSlice();
    }

    this.shipmentItemId = shipmentItemId;
    this.displayShipmentItemDetailsModalbox = true;
  }

  private updateInMemoryPageSlice(): void {
    const src = this.itemsList || [];

    this.inMemoryTotalRecords = src.length;

    const { first, rows } = this.inMemoryPaginator;

    this.inMemoryPagedResults = src.slice(first, first + rows);
    this.isResultsEmpty = this.inMemoryTotalRecords === 0;
  }

  private resetInMemoryPaginatorAndSlice(): void {
    this.inMemoryPaginator.first = 0;
    this.updateInMemoryPageSlice();
  }

  private resetShipmentItemsPaginator(): void {
    this.paginatorFirst = 0;
  }

  onInMemoryPageChange(event: any): void {
    this.inMemoryPaginator.first = event.first ?? 0;
    this.inMemoryPaginator.rows = event.rows ?? this.inMemoryPaginator.rows;

    this.updateInMemoryPageSlice();
  }

  async onSelectItem(itemId: number): Promise<void> {
    const payload: ReviewShipmentItemRequest = {
      shipmentItemId: this.shipmentItemId,
      itemId
    };

    try {
      await firstValueFrom(this.shipmentsService.apiShipmentsReviewPut(payload));
    } catch (error) {
      console.error('Error reviewing shipment item', error);
    } finally {
      this.shipmentItemId = 0;
      this.displayShipmentItemDetailsModalbox = false;
      await this.get();
    }
  }

  getSelectedSerialNumbers(): string[] {
    return this.getSelectedItems().map((item: any) => item.serialNumber);
  }

  onScannerItemsChange(items: InventoryMultiScannerItem[]): void {
    this.addItemsFormGroup.patchValue({
      shipmentItems: items.map(item => ({
        serialNumber: item.serialNumber
      }))
    });

    this.addItemsFormGroup.get('shipmentItems')?.markAsDirty();
    this.addItemsFormGroup.get('shipmentItems')?.updateValueAndValidity();
  }

  async onScannerFinish(items: InventoryMultiScannerItem[]): Promise<void> {
    await this.multiScanner?.stopScanner();

    this.onScannerItemsChange(items);

    if (!items.length) {
      this.displayAddItemsModalbox = false;
      this.resetScannerDraft();
      return;
    }

    await this.onSubmitAddedItems();
  }

  async onScannerCancel(): Promise<void> {
    await this.multiScanner?.stopScanner();

    this.displayAddItemsModalbox = false;
    this.resetScannerDraft();
  }

  onScannerError(error: any): void {
    console.error('Shipment scanner error:', error);
  }

  private resetScannerDraft(): void {
    this.scannerInitialItems = [];

    this.addItemsFormGroup.patchValue({
      shipmentItems: [],
      serialNumber: ''
    });

    this.addItemsFormGroup.markAsPristine();
    this.addItemsFormGroup.updateValueAndValidity();
  }

  requestScannerModalClose(): void {
    if (this.hasUnsavedScannerItems()) {
      this.displayAddItemsModalbox = false;

      setTimeout(() => {
        this.displayUnsavedScannerItemsModalbox = true;
      });

      return;
    }

    this.onScannerCancel();
  }

  async dismissUnsavedScannerItems(): Promise<void> {
    this.displayUnsavedScannerItemsModalbox = false;
    // await this.onScannerCancel();
  }

  async saveUnsavedScannerItems(): Promise<void> {
    this.displayUnsavedScannerItemsModalbox = false;

    await this.onSubmitAddedItems();
  }

  private hasUnsavedScannerItems(): boolean {
    return this.getSelectedItems().length > 0;
  }

  ngOnDestroy(): void {
    this.destroyedComponent$.next();
    this.destroyedComponent$.complete();
  }
}
